// @flow

import Client from 'braintree-web/client';
import PaypalCheckout from 'braintree-web/paypal-checkout';
import paypal from 'paypal-checkout';
import React, { Component } from 'react';
import { prop } from 'lodash/fp';

import {
  type PaymentDetailsAddress,
  type PaymentSourceInput,
  paymentSourceBackends,
  paymentSourceMethods,
} from '@zapier/payment/src/types';

import PaypalButtonPlaceholder from './PaypalButtonPlaceholder';
import { PAYPAL_BUTTON_ID } from './constants';

import type { Spacing } from '@zapier/common-utils';

type Props = {
  /** Paypal API Key */
  apiKey: string,
  /** Call when Paypal has authorized user */
  onAuthorize: PaymentSourceInput => void,
  /** Call when Paypal returns an error */
  onError: Error => void,
  /** Margin to apply to button wrapper */
  margin?: Spacing,
};

type State = {
  isLoading: boolean,
};

type PaypalCheckoutPayload = {
  billingAddress: PaymentDetailsAddress,
  email: string,
  nonce: string,
};

type PaypalCheckoutType = {
  createPayment: ({}) => {},
  tokenizePayment: ({}) => Promise<PaypalCheckoutPayload>,
};

const PAYMENT_DESC = {
  billingAgreementDescription: `
    You authorize Zapier to charge your PayPal account for your chosen subscription at
    your specified billing interval.
    By continuing, you understand and agree to these terms and Zapier’s privacy policy
    and terms of service.
  `,
  flow: 'vault',
};

const BUTTON_STYLE = {
  color: 'blue',
  label: 'paypal',
  shape: 'rect',
  size: 'medium',
  height: 49,
  tagline: false,
};

class PaypalButton extends Component<Props, State> {
  paypalCheckout: PaypalCheckoutType;

  state = {
    isLoading: true,
  };

  createPayment = () => {
    return this.paypalCheckout.createPayment(PAYMENT_DESC);
  };

  handleAuthorize = async (data: {}) => {
    const payload = await this.paypalCheckout.tokenizePayment(data);
    const billingAddress = prop(['details', 'billingAddress'], payload);

    const paymentSource = {
      details: {
        addressCountry: billingAddress.countryCode,
        addressState: billingAddress.state,
        addressZip: billingAddress.postalCode,
        email: prop(['details', 'email'], payload),
      },
      type: paymentSourceMethods.paypal,
      paymentProcessor: paymentSourceBackends.braintree,
      token: payload.nonce,
    };

    this.props.onAuthorize(paymentSource);
  };

  handleError = (error: Error) => {
    this.props.onError(error);
  };

  async componentDidMount() {
    const { apiKey } = this.props;

    const client = await Client.create({
      authorization: apiKey,
    });

    this.paypalCheckout = await PaypalCheckout.create({ client });
    this.setState({ isLoading: false });

    await paypal.Button.render(
      {
        env: apiKey.split('_')[0],
        onAuthorize: this.handleAuthorize,
        onError: this.handleError,
        payment: this.createPayment,
        style: BUTTON_STYLE,
      },
      `#${PAYPAL_BUTTON_ID}`
    );
  }

  render() {
    return (
      <PaypalButtonPlaceholder
        isLoading={this.state.isLoading}
        margin={this.props.margin}
      />
    );
  }
}

export default PaypalButton;
